// Enhanced VideoList.js with stability fixes
import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef, useCallback, memo } from "react";
import "./VideoList.css";
import { getRobotEvents } from "../TIMEBAR/robotEvents";
import { useEventContext } from "../TIMEBAR/EventContext";
import { useAppStore } from "../../store/appStore";

const getOverlayImageByType = (type) => {
  switch (type) {
    case 1: return "/animations/ic.png";
    case 2: return "/animations/ip.png";
    case 3: return "/animations/io.png";
    case 4: return "/animations/uc.png";
    case 5: return "/animations/up.png";
    case 6: return "/animations/uo.png";
    default: return "/animations/default.png";
  }
};

// Using memo to optimize video item rendering
const VideoItem = memo(({ 
  video, 
  index, 
  isActive, 
  isSelected, 
  overlayImage,
  hasActiveEvent,
  videoRef,
  handleVideoSelect,
  toggleVideoSelection,
  handleDragStart
}) => {
  // Keep track of item DOM element for stable dimensions
  const itemRef = useRef(null);
  
  // Use effect to preserve dimensions during active state changes
  useEffect(() => {
    if (itemRef.current && isActive) {
      // Store original dimensions to prevent layout shift
      const rect = itemRef.current.getBoundingClientRect();
      itemRef.current.style.height = `${rect.height}px`;
      
      return () => {
        // Clean up the fixed height when component updates
        if (itemRef.current) {
          itemRef.current.style.height = '';
        }
      };
    }
  }, [isActive]);

  return (
    <div
      ref={itemRef}
      className={`video-item 
        ${isActive ? 'active' : ''} 
        ${isSelected ? 'selected' : ''}`
      }
      onClick={() => handleVideoSelect(index)}
    >
      <div className="video-player-wrapper">
        {hasActiveEvent && overlayImage && (
          <img
            src={overlayImage}
            alt="event alert"
            className="event-overlay-image"
          />
        )}

        <div className="video-label">{`robot${index + 1}`}</div>

        {/* Toggle switch button */}
        <div className="ios-toggle-container">
          <div 
            className={`ios-toggle ${isSelected ? 'on' : 'off'}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleVideoSelection(index);
            }}
            onMouseDown={(e) => handleDragStart(e, index)}
          >
            <div className="ios-toggle-track"></div>
            <div className="ios-toggle-button"></div>
            <span className="ios-toggle-text">
              {isSelected ? 'Show' : 'Hide'}
            </span>
          </div>
        </div>

        <video
          ref={(el) => videoRef(el, index)}
          id={`videoPlayer-${index}`}
          src={video}
          muted
          preload="metadata"
          width="100%"
          height="auto"
          onClick={(e) => {
            e.stopPropagation();
            handleVideoSelect(index);
          }}
        />
      </div>
    </div>
  );
});

const VideoList = forwardRef((props, ref) => {
  const { videoFiles = [], highResVideoFiles = [], onVideoSelectionChange, onTimeframeChange, onVideoSelect } = props;
  
  // Get global state from Zustand
  const currentGlobalTime = useAppStore(state => state.videoState.currentTime);
  const isGlobalPlaying = useAppStore(state => state.videoState.isPlaying);
  const activeGlobalRobotId = useAppStore(state => state.selection.activeRobotId);
  const updateVideoState = useAppStore(state => state.updateVideoState);
  
  // Local state
  const [activeVideoIndex, setActiveVideoIndex] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [timeframe, setTimeframe] = useState("11AM");
  const [selectedVideos, setSelectedVideos] = useState({});
  const [videoEvents, setVideoEvents] = useState({});
  const [dragStartX, setDragStartX] = useState(null);
  const [isVideoSwitching, setIsVideoSwitching] = useState(false); // Added to track switching state
  
  // Refs
  const videoRefs = useRef([]);
  const syncInProgressRef = useRef(false);
  const rafIdRef = useRef(null);
  const isComponentMounted = useRef(true);
  const lastUpdateTimeRef = useRef(0);
  const timeUpdateThresholdRef = useRef(1000 / 30); // 30fps

  const videoSwitchInProgressRef = useRef(false);
  const lastVideoSwitchTimeRef = useRef(0);
  const videoListRef = useRef(null); // Reference to main container
  
  // Use EventContext to get activeRobotId and setActiveEvent
  const { activeRobotId, setActiveEvent } = useEventContext();
  
  // Component mount/unmount handling
  useEffect(() => {
    isComponentMounted.current = true;
    
    return () => {
      isComponentMounted.current = false;
      
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      
      // Ensure all videos are paused
      videoRefs.current.forEach(videoRef => {
        if (videoRef && !videoRef.paused) {
          videoRef.pause();
        }
      });
    };
  }, []);
  
  // Load video event data
  useEffect(() => {
    try {
      const parsed = {};
      videoFiles.forEach((_, index) => {
        const robotId = index + 1;
        const csvText = getRobotEvents(robotId, timeframe);
        const lines = csvText.trim().split("\n").slice(1);
        parsed[index] = lines.map(line => {
          const parts = line.split(",");
          const type = parseInt(parts[0]);
          const frame_start = parseInt(parts[2]);
          const frame_end = parseInt(parts[3]);
          return { frame_start, frame_end, type };
        });
      });
      setVideoEvents(parsed);
    } catch (error) {
      console.error("Failed to load video event data:", error);
    }
  }, [timeframe, videoFiles]);

  // Initialize all videos as selected
  useEffect(() => {
    const initialState = {};
    videoFiles.forEach((_, index) => {
      initialState[index] = true;
    });
    setSelectedVideos(initialState);
  }, [videoFiles]);
  
  // Update video reference callback
  const setVideoRef = useCallback((el, index) => {
    videoRefs.current[index] = el;
  }, []);
  
  // Listen for global time changes, sync local time
  useEffect(() => {
    // Avoid ongoing sync
    if (syncInProgressRef.current) return;
    
    // Use throttling to control sync frequency
    const now = Date.now();
    if (now - lastUpdateTimeRef.current < timeUpdateThresholdRef.current) {
      return;
    }
    lastUpdateTimeRef.current = now;
    
    // If global time has significant change, update local time
    if (Math.abs(currentTime - currentGlobalTime) > 0.5) {
      setCurrentTime(currentGlobalTime);
      
      // Use requestAnimationFrame to optimize sync performance
      requestAnimationFrame(() => {
        syncAllVideosToTime(currentGlobalTime);
      });
    }
  }, [currentGlobalTime, currentTime]);
  
  // Listen for global play state changes
  useEffect(() => {
    syncPlayState(isGlobalPlaying);
  }, [isGlobalPlaying]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Listen for activeRobotId changes, update activeVideoIndex
  useEffect(() => {
    if (activeGlobalRobotId && activeGlobalRobotId > 0 && activeGlobalRobotId <= videoFiles.length) {
      setActiveVideoIndex(activeGlobalRobotId - 1);
    }
  }, [activeGlobalRobotId, videoFiles.length]);
  
  // Sync all videos to specified time
  const syncAllVideosToTime = useCallback((time) => {
    if (syncInProgressRef.current) return;
    
    syncInProgressRef.current = true;
    
    videoRefs.current.forEach((videoRef, index) => {
      if (!videoRef || !selectedVideos[index]) return;
      
      try {
        // Add time difference threshold to reduce unnecessary updates
        if (Math.abs(videoRef.currentTime - time) > 0.5) {
          videoRef.currentTime = time;
        }
      } catch (err) {
        console.warn(`Error updating video ${index} time:`, err);
      }
    });
    
    syncInProgressRef.current = false;
  }, [selectedVideos]);
  
  // Sync all videos' play state, optimized handling logic
  const syncPlayState = useCallback((isPlaying) => {
    // Use requestAnimationFrame to optimize play state sync
    requestAnimationFrame(() => {
      videoRefs.current.forEach((videoRef, index) => {
        if (!videoRef || !selectedVideos[index]) return;
        
        try {
          if (isPlaying && videoRef.paused) {
            // Use play() Promise to ensure successful playback
            const playPromise = videoRef.play();
            if (playPromise !== undefined) {
              playPromise.catch(err => {
                console.warn(`Video ${index} playback failed:`, err);
                // If playback fails, try muted playback
                if (err.name === 'NotAllowedError') {
                  videoRef.muted = true;
                  videoRef.play().catch(() => {});
                }
              });
            }
          } else if (!isPlaying && !videoRef.paused) {
            videoRef.pause();
          }
        } catch (err) {
          console.warn(`Video ${index} state sync error:`, err);
        }
      });
    });
  }, [selectedVideos]);

  // Add video performance optimization
  useEffect(() => {
    videoRefs.current.forEach(videoRef => {
      if (videoRef) {
        // Set video preload
        videoRef.preload = "metadata"; // Changed to metadata to reduce memory usage
        
        // Optimize video decoding
        videoRef.playsInline = true;
        videoRef.disablePictureInPicture = true;
        
        // Set video playback rate
        videoRef.playbackRate = 1.0;
        
        // Listen for video playback rate changes
        const handleRateChange = () => {
          if (videoRef.playbackRate !== 1.0) {
            videoRef.playbackRate = 1.0;
          }
        };
        
        videoRef.addEventListener('ratechange', handleRateChange);
        
        return () => {
          videoRef.removeEventListener('ratechange', handleRateChange);
        };
      }
    });
  }, []);

  // Handle timeframe change
  const handleTimeframeChange = (e) => {
    const newTimeframe = e.target.value;
    setTimeframe(newTimeframe);
    
    // Pause all videos
    videoRefs.current.forEach(videoRef => {
      if (videoRef && !videoRef.paused) {
        videoRef.pause();
      }
    });
    
    if (onTimeframeChange) {
      onTimeframeChange(newTimeframe);
    }
  };

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    handleSliderChange: (newTime) => {
      // Update local time state
      setCurrentTime(newTime);
      
      // Sync all videos to new time
      syncAllVideosToTime(newTime);
    },
    
    getSelectedVideos: () => selectedVideos,
    
    getCurrentTimeframe: () => timeframe,
    
    syncPlayState: (isPlaying) => {
      syncPlayState(isPlaying);
    }
  }));

  // Enhanced handleVideoSelect function for VideoList
  const handleVideoSelect = useCallback(async (index) => {
    const timestamp = new Date().toISOString();
    
    // Fixed 1: Preserve container dimensions during video switch to prevent jumping
    if (videoListRef.current) {
      const container = videoListRef.current;
      const rect = container.getBoundingClientRect();
      container.style.height = `${rect.height}px`;
    }
    
    // Fixed 2: Prevent rapid consecutive video switches
    if (videoSwitchInProgressRef.current) {
      console.log(`[VideoList ${timestamp}] ⏭️ Video switch in progress, ignoring new request`);
      return;
    }
    
    // Avoid unnecessary reselection of current video
    if (index === activeVideoIndex && !isVideoSwitching) {
      console.log(`[VideoList ${timestamp}] ℹ️ Skipping selection of same video`);
      
      // Still release the fixed height after a short delay
      setTimeout(() => {
        if (videoListRef.current) {
          videoListRef.current.style.height = '';
        }
      }, 100);
      
      return;
    }
    
    // Enforce minimum time between video switches (300ms)
    if (Date.now() - lastVideoSwitchTimeRef.current < 300) {
      console.log(`[VideoList ${timestamp}] ⏭️ Ignoring rapid video switch, only ${Date.now() - lastVideoSwitchTimeRef.current}ms since last`);
      
      // Release container height
      setTimeout(() => {
        if (videoListRef.current) {
          videoListRef.current.style.height = '';
        }
      }, 100);
      
      return;
    }
    
    // Set switching state and locks
    setIsVideoSwitching(true);
    videoSwitchInProgressRef.current = true;
    lastVideoSwitchTimeRef.current = Date.now();
    console.log(`[VideoList ${timestamp}] 🔄 Starting video switch to index ${index}`);
    
    try {
      // Update active video index in local state
      setActiveVideoIndex(index);
      
      // Calculate robotId (index+1)
      const robotId = index + 1;
      
      // Pause all videos first
      console.log(`[VideoList ${timestamp}] ⏸️ Pausing all videos before switch`);
      videoRefs.current.forEach((videoRef) => {
        if (videoRef && !videoRef.paused) {
          try {
            videoRef.pause();
          } catch (e) {
            // Ignore errors
          }
        }
      });
      
      // Get video URL - use high-res version or default URL
      const timePart = timeframe === "11AM" ? "day" : "night";
      const highResVideoUrl = highResVideoFiles && highResVideoFiles[index] 
        ? highResVideoFiles[index]
        : `/${timePart}/videos/video${robotId}.mp4`;
      
      // IMPORTANT FIX: Get current time before switching
      // This ensures we preserve the current playback position
      const currentTimePosition = currentTime;
      
      console.log(`[VideoList ${timestamp}] 📊 Updating global state: robotId=${robotId}, preserving time=${currentTimePosition}`);
      
      // First update with isPlaying=false and new source
      // IMPORTANT FIX: Keep the current time position in the update
      updateVideoState({
        robotId: robotId,
        videoUrl: highResVideoUrl,
        isPlaying: false,
        lastVideoSwitch: null,
        time: currentTimePosition // Explicitly preserve time
      }, 'videoList_select');
      
      // Update in EventContext as well with preserved time
      setActiveEvent(null, robotId, currentTimePosition);
      
      // Wait slightly longer (100ms) to ensure video has time to load
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Then resume playback in a separate update
      // IMPORTANT FIX: Include the time again to make sure it persists through the playback state change
      console.log(`[VideoList ${timestamp}] ▶️ Resuming playback at time ${currentTimePosition}`);
      updateVideoState({
        isPlaying: true,
        lastVideoSwitch: null,
        time: currentTimePosition // Reinforce time preservation
      }, 'videoList_resume');
      
      // Call parent component callback if provided
      if (onVideoSelect) {
        onVideoSelect(highResVideoUrl, currentTimePosition);
      }
    } catch (error) {
      console.error(`[VideoList ${timestamp}] ⚠️ Video switch error:`, error);
    } finally {
      // Release lock with slight delay
      setTimeout(() => {
        videoSwitchInProgressRef.current = false;
        setIsVideoSwitching(false);
        console.log(`[VideoList ${timestamp}] ✅ Video switch completed, released lock`);
        
        // Release container height
        if (videoListRef.current) {
          videoListRef.current.style.height = '';
        }
      }, 200);
    }
  }, [activeVideoIndex, currentTime, highResVideoFiles, isVideoSwitching, onVideoSelect, setActiveEvent, timeframe, updateVideoState]);

  // Add cleanup function
  useEffect(() => {
    return () => {
      // Reset video switch flags when unmounting
      videoSwitchInProgressRef.current = false;
      setIsVideoSwitching(false);
      
      // Release container height
      if (videoListRef.current) {
        videoListRef.current.style.height = '';
      }
    };
  }, []);

  // Toggle video selection state
  const toggleVideoSelection = useCallback((index) => {
    const newSelection = !selectedVideos[index];
    setSelectedVideos(prev => ({
      ...prev,
      [index]: newSelection
    }));
    
    if (onVideoSelectionChange) {
      onVideoSelectionChange(index, newSelection);
    }
  }, [selectedVideos, onVideoSelectionChange]);

  // Drag start
  const handleDragStart = useCallback((e, index) => {
    e.preventDefault();
    setDragStartX({ x: e.clientX, index });
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  }, []);

  // Drag move
  const handleDragMove = useCallback((e) => {
    if (dragStartX) {
      const deltaX = e.clientX - dragStartX.x;
      // If slide exceeds threshold, toggle state
      if (Math.abs(deltaX) > 20) {
        const isMovingRight = deltaX > 0;
        const currentState = selectedVideos[dragStartX.index];
        
        // Toggle state based on drag direction
        if ((isMovingRight && !currentState) || (!isMovingRight && currentState)) {
          toggleVideoSelection(dragStartX.index);
          // Reset drag position to prevent multiple triggers
          setDragStartX(null);
          document.removeEventListener('mousemove', handleDragMove);
          document.removeEventListener('mouseup', handleDragEnd);
        }
      }
    }
  }, [dragStartX, selectedVideos, toggleVideoSelection]);

  // Drag end
  const handleDragEnd = useCallback(() => {
    setDragStartX(null);
    document.removeEventListener('mousemove', handleDragMove);
    document.removeEventListener('mouseup', handleDragEnd);
  }, [handleDragMove]);

  // Render video list
  return (
    <div className="video-list-wrapper" ref={videoListRef}>
      <div className="timeframe-selector">
        <select
          value={timeframe}
          onChange={handleTimeframeChange}
          className="timeframe-select"
        >
          <option value="11AM">Day 11AM - 11:30AM</option>
          <option value="11PM">Night 9:30PM - 10:00PM</option>
        </select>
      </div>

      <div className="video-list-container">
        {videoFiles.map((video, index) => {
          // Find current active event
          const activeEvent = (videoEvents[index] || []).find(event => {
            const currentFrame = currentTime * 30;
            return currentFrame >= event.frame_start && currentFrame <= event.frame_end;
          });

          const isEventActive = !!activeEvent;
          const overlayImage = activeEvent ? getOverlayImageByType(activeEvent.type) : null;
          
          return (
            <VideoItem
              key={index}
              video={video}
              index={index}
              isActive={activeVideoIndex === index}
              isSelected={selectedVideos[index]}
              overlayImage={overlayImage}
              hasActiveEvent={isEventActive}
              videoRef={setVideoRef}
              handleVideoSelect={handleVideoSelect}
              toggleVideoSelection={toggleVideoSelection}
              handleDragStart={handleDragStart}
            />
          );
        })}
      </div>
    </div>
  );
});

export default VideoList;